// catalogDisplay — the products surface's presentational pieces (wire item →
// pixels): tier badge, capability tags, the rating readout, and the reference
// table's COLUMN FACTORY + band headers. Table-first (architect-ruled at the
// catalog step-back): flat arrives by default (the other pages' arrival), the
// gear's grouping knob offers Category / Category → Series bands (the store's
// shelves moved into the grid); the card face retired at 0% photo coverage
// (the gallery face returns for judging when documents packs bring real
// photos). Reference-table posture below md: the fit ladder drops trailing
// facts one at a time; identity never drops (FRONTEND-PATTERNS §Scroll doctrine).

import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import type { AssetCategory } from '@ems-app/schemas/assets/categories';
import { isNetworkCapable } from '@ems-app/schemas/assets/categories';
import type { AssetModel, AssetModelProjectItem } from '@ems-app/schemas/catalog/responses';
import { CATEGORY_ICON } from './assetsDisplay.js';
import { EntityRef } from './EntityRef.js';
import { Badge } from '../ui/Badge.js';
import { InlineCopy } from '../ui/CopyField.js';
import type { DataTableColumn } from '../ui/DataTable.js';
import { Tag } from '../ui/Tag.js';
import { formatNumber } from '../../lib/format.js';

/** The one networked predicate every product surface shares (the wizard's
 *  inherit-vs-override law): the row's own flag, falling back to the
 *  category's connectivity default — plus a data-honesty arm (declared
 *  capabilities/interfaces stay visible/editable even if the flag was later
 *  turned off, so nothing becomes unreachable). */
export function showsDeviceHalf(m: AssetModelProjectItem): boolean {
  const resolved = isNetworkCapable(m.category as AssetCategory, m.is_network_device);
  return resolved || m.capabilities.length > 0 || Object.keys(m.interfaces).length > 0;
}

// Base-model input (not the project item) — the platform surfaces (merge facts)
// read the same headline without a project context.
export function ratingOf(m: AssetModel): string | null {
  if (m.power_kw != null) return `${m.power_kw} kW`;
  if (m.capacity_kva != null) return `${m.capacity_kva} kVA`;
  if (m.capacity_ah != null) return `${m.capacity_ah} Ah${m.voltage_v != null ? ` · ${m.voltage_v} V` : ''}`;
  if (m.watt_peak_w != null) return `${m.watt_peak_w} Wp`;
  if (m.cooling_kw != null) return `${m.cooling_kw} kW`;
  if (m.tank_l != null) return `${m.tank_l} L`;
  return null;
}

/** The rating's sort number — same ladder as the readout; units are
 *  homogeneous WITHIN a category band, which is the only place the sort acts. */
function ratingValue(m: AssetModelProjectItem): number | null {
  return m.power_kw ?? m.capacity_kva ?? m.capacity_ah ?? m.watt_peak_w ?? m.cooling_kw ?? m.tank_l;
}

export function TierBadge({ m }: { m: AssetModel }) {
  const { t } = useTranslation('catalog');
  if (m.is_canonical) return null;
  return (
    <Badge variant="info" appearance="subtle" title={t('store.tierLocalHint')}>
      {t('store.tierLocal')}
    </Badge>
  );
}

export function CapTags({ m, max = 3 }: { m: AssetModelProjectItem; max?: number }) {
  const { t } = useTranslation('assets');
  if (m.capabilities.length === 0) return null;
  const shown = m.capabilities.slice(0, max);
  return (
    <span className="flex flex-wrap gap-1">
      {shown.map((c) => (
        <Tag key={c}>{t(`capability.${c}`)}</Tag>
      ))}
      {m.capabilities.length > max && <Tag>+{m.capabilities.length - max}</Tag>}
    </span>
  );
}

const emDash = <span className="text-[var(--color-text-secondary)]">—</span>;

/** The products table's columns. The identity cell is TEXT-ONLY (the register's
 *  row discipline — the category glyph lives on the sticky band, never repeated
 *  per row; the photo face returns with real imagery). Eyebrow: grouped, only a
 *  series-less tail row names its maker (banded rows would stutter the band);
 *  flat (grouping off), every row carries category · maker · series — the bands'
 *  information moves into the row. Drop ladder (first → last): capabilities →
 *  part number → used → rating; the tight phone face is identity + rating. */
export function productColumns(
  t: TFunction<'catalog'>,
  ta: TFunction<'assets'>,
  onOpen: (id: string) => void,
  grouped: boolean,
): DataTableColumn<AssetModelProjectItem>[] {
  const identityCell = (m: AssetModelProjectItem) => {
    const eyebrow = grouped
      ? m.series
        ? null
        : m.manufacturer
      : [ta(`category.${m.category}`), m.manufacturer, m.series].filter(Boolean).join(' · ');
    // Flat rows carry the category glyph (mixed categories per screen — it
    // earns its place); grouped rows stay text-only (the band owns the glyph).
    const Glyph = grouped ? null : CATEGORY_ICON[m.category as AssetCategory];
    const body = (
      <span className="min-w-0 block">
        <span className="flex items-center gap-1.5">
          <EntityRef name={m.name} onOpen={() => onOpen(m.id)} />
          <TierBadge m={m} />
          {m.is_disabled && (
            <Badge variant="neutral" appearance="subtle">
              {t('store.notOffered')}
            </Badge>
          )}
        </span>
        {eyebrow && <span className="block truncate text-xs text-[var(--color-text-secondary)]">{eyebrow}</span>}
      </span>
    );
    if (!Glyph) return body;
    return (
      <span className="flex items-center gap-2">
        <Glyph size={18} aria-hidden className="shrink-0 text-[var(--color-text-secondary)]" />
        {body}
      </span>
    );
  };
  return [
    // Band keys (grouping-only — paint nothing): Category → Series; a series-less
    // row groups under '' and renders bandless directly beneath its category.
    { id: 'category', header: '', groupingOnly: true, groupValue: (m) => m.category, cell: () => null },
    { id: 'series', header: '', groupingOnly: true, groupValue: (m) => m.series ?? '', cell: () => null },
    {
      id: 'product',
      header: t('store.colProduct'),
      neverDrop: true,
      floor: '15rem',
      degraded: { minWidth: '11rem', cell: identityCell },
      sortable: true,
      value: (m) => `${m.manufacturer ?? ''} ${m.name}`,
      cell: identityCell,
    },
    {
      id: 'rating',
      header: t('store.colRating'),
      label: t('store.colRating'),
      align: 'end',
      dropOrder: 40,
      minWidth: '6rem',
      sortable: true,
      sortDescFirst: true,
      value: (m) => ratingValue(m),
      cell: (m) => ratingOf(m) ?? emDash,
    },
    {
      id: 'capabilities',
      header: t('store.colCapabilities'),
      label: t('store.colCapabilities'),
      floor: '11rem',
      dropOrder: 10,
      cell: (m) => (m.capabilities.length > 0 ? <CapTags m={m} max={2} /> : emDash),
    },
    {
      id: 'part_number',
      header: t('store.colPartNumber'),
      label: t('store.colPartNumber'),
      minWidth: '8rem',
      dropOrder: 20,
      cell: (m) => (m.part_number ? <InlineCopy value={m.part_number} truncate="middle" /> : emDash),
    },
    {
      id: 'used',
      header: t('store.colUsed'),
      label: t('store.colUsed'),
      align: 'end',
      minWidth: '5.5rem',
      dropOrder: 30,
      sortable: true,
      sortDescFirst: true,
      value: (m) => m.in_use_count,
      cell: (m) => (m.in_use_count > 0 ? formatNumber(m.in_use_count) : emDash),
    },
  ];
}

/** Band headers: category = glyph + word + count pill (the register's band
 *  recipe — the glyph's ONE home, riding the sticky band); series = the family
 *  word + its maker (when uniform and not the family's own name) + count pill;
 *  a series-less bucket wears the explicit "No series" words (architect-ruled:
 *  a clear dummy group at the end of the category, never bandless rows). */
export function productGroupHeader(t: TFunction<'catalog'>, ta: TFunction<'assets'>) {
  const pill = (n: number) => (
    <span className="rounded-full bg-[var(--color-bg-surface-alt)] px-1.5 py-px text-xs font-medium tabular-nums text-[var(--color-text-secondary)]">
      {formatNumber(n)}
    </span>
  );
  return function header(value: string, rows: AssetModelProjectItem[], columnId?: string) {
    if (columnId === 'series') {
      if (value === '') {
        return (
          <>
            <span className="font-normal text-[var(--color-text-secondary)]">{t('store.noSeries')}</span>
            {pill(rows.length)}
          </>
        );
      }
      const makers = new Set(rows.map((m) => m.manufacturer ?? ''));
      const maker = makers.size === 1 ? [...makers][0] : null;
      const showMaker = maker && maker.toLowerCase() !== value.toLowerCase();
      return (
        <>
          <span dir="ltr">{value}</span>
          {showMaker ? <span className="font-normal text-[var(--color-text-secondary)]">· {maker}</span> : null}
          {pill(rows.length)}
        </>
      );
    }
    const Glyph = CATEGORY_ICON[value as AssetCategory];
    return (
      <>
        {Glyph && <Glyph size={16} aria-hidden className="shrink-0 text-[var(--color-text-secondary)]" />}
        {ta(`category.${value}`)}
        {pill(rows.length)}
      </>
    );
  };
}
